import ElasticComponentContext from '@/features/shared/ElasticComponent/ElasticComponentContext';
import { datalayerPush } from '@/features/shared/HOCs/withGTM';
import { useIsRevision } from '@/features/shared/Hooks';
import { useDataLayerSearchEvents } from '@/features/shared/Hooks/data-layer';
import { MuiButton, MuiMenu, MuiMenuItem, MuiTypography } from '@/features/shared/material-ui';
import { LoadingStatusValues } from '@/utils/constants';
import { formatText, getNextId } from '@/utils/helpers';
import { ExpandMore as ExpandMoreIcon } from '@material-ui/icons';
import clsx from 'clsx';
import qs from 'query-string';
import React, { MouseEvent, PropsWithChildren, useContext, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { SearchResultProps } from '../SearchResult.interface';
import useStyles from './Header.styles';
import { useTriggerPortalEvent } from '@/features/shared/DataLayerLoginLogout/useTriggerPortalEvent';
import { getPortalPageLoadSearchResult } from '@/features/SearchResult/SearchResult.datalayer';

const HeaderContent = ({
  query,
  isSortShown = true,
}: PropsWithChildren<Partial<SearchResultProps>>) => {
  const idSearchResultHeaderSortBy = useRef(getNextId('search-result-header-sort-by-'));
  const classes = useStyles({});
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [dataLayerId, setDataLayerId] = React.useState('');
  const {
    queryParams,
    updateQueryParams,
    sortOptions,
    pagination,
    labels = {},
    status,
  } = useContext(ElasticComponentContext);
  const location = useLocation();
  const [sort, setSort] = useState(sortOptions.find((item) => item.key === queryParams.sortBy));
  const containerRef = useRef<HTMLDivElement>(null);
  const isRevision = useIsRevision();
  const { triggerSearchEventDataLayer } = useDataLayerSearchEvents();
  const [isFirstLoad, setIsFirstLoad] = React.useState(true);
  const { triggerPortalDataLayerEvent } = useTriggerPortalEvent();

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const variantName = (containerRef?.current?.closest('[data-datalayer-variant]') as HTMLDivElement)
    ?.dataset?.datalayerVariant;

  const handleSort = (option: { key: string; value: string }) => {
    setSort(option);

    datalayerPush({
      compRef: containerRef,
      event: 'gtm:custom:sort',
      args: {
        params: {
          sortOption: option.key,
          variantName,
        },
      },
    });

    updateQueryParams('sortBy', option.key);
    triggerSearchEventDataLayer();
    handleClose();
  };

  React.useEffect(() => {
    if (dataLayerId !== 'query_search') return;
    if (isFirstLoad && status !== LoadingStatusValues.ready) return;
    if (!isFirstLoad && status !== LoadingStatusValues.loading) return;

    const { q: searchTerm = '' } = qs.parse(location.search);
    const indexNum = String(pagination?.currentPage || 1);

    datalayerPush({
      compRef: containerRef,
      event: 'gtm:custom:query-search',
      args: {
        searchTerm,
        totalResults: pagination.totalResults,
        indexNum,
        variantName,
        isRevision,
      },
    });

    triggerPortalDataLayerEvent(
      getPortalPageLoadSearchResult({
        isRevision,
        totalResults: pagination.totalResults,
        indexNum,
      }),
    );

    setDataLayerId('');
    setIsFirstLoad(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, dataLayerId]);

  React.useLayoutEffect(() => {
    if (queryParams?.q) {
      setDataLayerId('query_search');
    } else {
      setDataLayerId('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams?.q]);

  const SHOWING_RESULTS = React.useMemo(
    () =>
      labels?.showing_results
        ? formatText(labels?.showing_results, [
            String(pagination?.from),
            String(pagination?.to),
            String(pagination?.totalResults),
          ])
        : `Showing <strong>${pagination?.from} - ${pagination?.to}</strong> of <strong>${pagination?.totalResults}</strong> Results`,
    [labels?.showing_results, pagination?.from, pagination?.to, pagination?.totalResults],
  );

  return (
    <div className={classes.root} ref={containerRef}>
      <div className={classes.heading}>
        <MuiTypography
          variant="h2"
          component="span"
          className={clsx(classes.query, !query && 'is-empty')}
        >
          {query ? `“${query}”` : <span>&nbsp;</span>}
        </MuiTypography>
        <MuiTypography
          variant="body1"
          component="span"
          className={classes.totalResults}
          dangerouslySetInnerHTML={{ __html: SHOWING_RESULTS }}
        />
      </div>
      {isSortShown && (
        <div className={classes.sortBy}>
          <MuiTypography variant="overline">{labels?.sort_by || 'Sort by'}</MuiTypography>
          <MuiButton
            aria-controls={idSearchResultHeaderSortBy.current}
            aria-haspopup="true"
            className={classes.sortByButton}
            onClick={handleClick}
            variant="text"
            endIcon={<ExpandMoreIcon className={classes.menuIcon} />}
          >
            {sort?.value}
          </MuiButton>
          <MuiMenu
            id={idSearchResultHeaderSortBy.current}
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            {sortOptions.map((item: any) => (
              <MuiMenuItem
                selected={item.key === sort}
                key={item.key}
                onClick={() => handleSort(item)}
              >
                {item.value}
              </MuiMenuItem>
            ))}
          </MuiMenu>
        </div>
      )}
    </div>
  );
};

export default HeaderContent;
